import {useState} from 'react';

import {isDefinedAndPopulated} from 'GlobalShared/utils/typeGuards';

import type {ImageUpload} from 'types/payload-types';

/**
 * The `useUspModule` hook manages the carousel state and computes the aspect ratio for the USP module's image display.
 * It tracks the currently active slide index and provides a setter to update it when the user navigates between slides.
 * Additionally, it derives a CSS aspect-ratio string from the provided image dimensions, falling back to a 1:1 ratio if the image is not populated.
 *
 * @param image The optional product image upload object used to calculate the display aspect ratio from its width and height.
 * @returns An object containing `activeSlideIndex` as the current slide position, `aspectRatio` as a CSS aspect-ratio string, and `setActiveSlideIndex` as a state setter to update the active slide.
 *
 * @example
 * ```tsx
 * const {activeSlideIndex, aspectRatio, setActiveSlideIndex} = useUspModule(image);
 * ```
 */
export const useUspModule = (image?: ImageUpload) => {
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);
    let aspectRatio = '1 / 1';

    if (isDefinedAndPopulated(image?.image)) {
        aspectRatio = `${image.image.width} / ${image.image.height}`;
    }

    return {
        activeSlideIndex,
        aspectRatio,
        setActiveSlideIndex
    };
};